/**
 * Threshold below which two GPS coordinates are considered the same location.
 * 0.0001 degrees ≈ 11 m at the equator — appropriate for GT06 noise floor.
 */
export const SAME_LOC = 0.0001;

/**
 * Adjacent dedup: walk the array, keep the first point, and include each
 * subsequent point only if it differs from the previous KEPT point by at
 * least SAME_LOC on either lat or lng.
 *
 * NOTE — semantic difference vs. the inline filter currently used inside
 * `updateHistoricalTrail` in MapView.jsx: that filter drops a point only
 * when BOTH neighbours (prev AND next) are within SAME_LOC, preserving
 * isolated noisy points and the trail terminus. This adjacent dedup is the
 * simpler "running last-kept" variant intended for the upcoming
 * VehicleMotion class. The inline filter is kept as-is for now (importing
 * SAME_LOC) because its semantics differ.
 *
 * @param {Array<{lat: number, lng: number}>} points
 * @returns {Array<{lat: number, lng: number}>} new array
 */
export function dedupBySameLocation(points) {
  if (!points || points.length === 0) return [];
  if (points.length === 1) return [points[0]];

  const result = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const prev = result[result.length - 1];
    const cur = points[i];
    if (
      Math.abs(cur.lat - prev.lat) >= SAME_LOC ||
      Math.abs(cur.lng - prev.lng) >= SAME_LOC
    ) {
      result.push(cur);
    }
  }
  return result;
}

/**
 * Variante newest-first de `dedupBySameLocation`, para los traces de
 * `useDevices` (index 0 = más reciente). Colapsa rachas de reportes
 * consecutivos en la misma ubicación conservando el MÁS NUEVO de cada racha
 * — la hermana de arriba, que recorre en orden cronológico, conserva el más
 * viejo. Esa es la única diferencia: ambas comparan siempre contra el último
 * punto CONSERVADO, nunca contra el último recibido, para que la deriva quede
 * acotada a SAME_LOC en vez de acumular.
 *
 * Por qué existe: la cola del monitor muestra las últimas N posiciones (N
 * chico, default 5) y un vehículo estacionado sigue reportando heartbeats con
 * la misma coordenada. Sin colapsarlos, cada heartbeat consume un slot de la
 * ventana y expulsa un punto real de la ruta — a los minutos de estacionarse
 * la cola son N copias del mismo punto y el tramo de llegada desaparece.
 * Conservar el más nuevo deja además el vértice final del trail donde está el
 * marcador, en vez de a ~11 m de deriva de jitter.
 *
 * Solo colapsa rachas ADYACENTES: dos visitas distintas al mismo lugar
 * separadas por movimiento se conservan ambas.
 *
 * Los puntos con lat/lng no finitos nunca se colapsan — se conservan tal cual
 * para que un punto corrupto no se coma a su vecino sano.
 *
 * @param {Array<{lat: number, lng: number}>} points  newest-first
 * @returns {Array<{lat: number, lng: number}>} new array
 */
export function dedupAdjacentSameLoc(points) {
  if (!Array.isArray(points) || points.length === 0) return [];
  if (points.length === 1) return [points[0]];

  const result = [points[0]];
  for (let i = 1; i < points.length; i += 1) {
    const kept = result[result.length - 1];
    const cur = points[i];
    if (
      Number.isFinite(cur?.lat) &&
      Number.isFinite(cur?.lng) &&
      Number.isFinite(kept?.lat) &&
      Number.isFinite(kept?.lng) &&
      Math.abs(cur.lat - kept.lat) < SAME_LOC &&
      Math.abs(cur.lng - kept.lng) < SAME_LOC
    ) {
      // Mismo lugar y más viejo que el punto ya conservado → la racha ya está
      // representada por su reporte más nuevo.
      continue;
    }
    result.push(cur);
  }
  return result;
}
